<template>
  <transition name="slide-fade">
    <div
      class="chat-modal"
      :class="{ 'input-focused': isInputFocused }"
      :style="keyboardHeight > 0 ? {
        bottom: `${keyboardHeight + 10}px`,
        '--keyboard-height': `${keyboardHeight}px`
      } : {}"
      v-if="modals.chat && currentChat"
      @paste="handlePaste"
    >
      <div class="chat-header">
        <div
          class="chat-title"
          @click="isMinimized ? toggleMinimize() : null"
          :class="{ clickable: isMinimized }"
        >
          <font-awesome-icon icon="comments" />
          {{ getChatTitle() }}
          <span
            v-if="isMinimized && currentChat && currentChat.unreadCount > 0"
            class="unread-badge-header"
          >
            {{ currentChat.unreadCount }}
          </span>
        </div>
        <div class="chat-controls">
          <font-awesome-icon
            :icon="isMinimized ? 'plus-circle' : 'minus-circle'"
            class="minimize-btn"
            @click="toggleMinimize"
          />
          <font-awesome-icon
            icon="times-circle"
            class="close-btn"
            @click="closeChat"
          />
        </div>
      </div>

      <!-- Screenshot Config Panel -->
      <div class="screenshot-config" v-if="screenshotPreview && !isMinimized">
        <div class="screenshot-preview">
          <img :src="screenshotPreview" alt="Screenshot preview" />
        </div>
        <div class="screenshot-options">
          <label class="radio-option">
            <input type="radio" value="timer" v-model="screenshotMode" />
            {{ $t("chat.screenshotTimer") }}
          </label>
          <label class="radio-option">
            <input type="radio" value="revoke" v-model="screenshotMode" />
            {{ $t("chat.screenshotRevoke") }}
          </label>
          <div class="ttl-input" v-if="screenshotMode === 'timer'">
            <label>{{ $t("chat.screenshotTTL") }}</label>
            <input type="number" v-model.number="screenshotTTL" min="3" max="120" />
            <span>{{ $t("chat.screenshotSeconds") }}</span>
          </div>
        </div>
        <div class="screenshot-actions">
          <div class="button cancel-btn" @click="cancelScreenshot">
            {{ $t("chat.cancel") }}
          </div>
          <div class="button send-btn" @click="sendScreenshot">
            <font-awesome-icon icon="paper-plane" />
            {{ $t("chat.sendScreenshot") }}
          </div>
        </div>
      </div>

      <div class="chat-messages" ref="messagesContainer" v-show="!isMinimized && !screenshotPreview">
        <div
          v-for="(message, index) in currentChat.messages"
          :key="index"
          class="message"
          :class="{
            sent: message.senderId === session.playerId,
            received: message.senderId !== session.playerId,
          }"
        >
          <!-- Text message -->
          <template v-if="!message.type || message.type === 'text'">
            <div class="message-sender">{{ message.senderName }}</div>
            <div class="message-content">{{ message.content }}</div>
            <div class="message-time">
              {{ formatTime(message.timestamp) }}
            </div>
          </template>

          <!-- Screenshot message -->
          <template v-else-if="message.type === 'screenshot'">
            <div class="message-sender">{{ message.senderName }}</div>
            <div class="screenshot-message">
              <!-- Expired/Revoked state -->
              <div v-if="message.revoked || isScreenshotExpired(message)" class="screenshot-expired">
                <font-awesome-icon icon="lock" />
                {{ $t("chat.screenshotExpired") }}
              </div>
              <!-- ST view: thumbnail + status -->
              <template v-else-if="isStoryteller">
                <div class="screenshot-thumbnail">
                  <img :src="message.imageData" alt="Screenshot" />
                </div>
                <div class="screenshot-status">
                  <template v-if="message.mode === 'timer'">
                    <span v-if="message.viewedAt">
                      <font-awesome-icon icon="clock" />
                      {{ getRemainingSeconds(message) }}{{ $t("chat.screenshotSecondsLeft") }}
                    </span>
                    <span v-else>
                      <font-awesome-icon icon="clock" />
                      {{ message.ttl }}{{ $t("chat.screenshotSeconds") }}
                    </span>
                  </template>
                  <div
                    v-if="message.mode === 'revoke'"
                    class="button revoke-btn"
                    @click="revokeScreenshot(message)"
                  >
                    <font-awesome-icon icon="trash-alt" />
                    {{ $t("chat.screenshotRevokeBtn") }}
                  </div>
                </div>
              </template>
              <!-- Player view: hidden state with View button -->
              <template v-else>
                <div v-if="!message.viewedAt" class="screenshot-hidden">
                  <font-awesome-icon icon="image" />
                  <div class="screenshot-label">{{ $t("chat.screenshotLabel") }}</div>
                  <div v-if="message.mode === 'timer'" class="screenshot-timer-info">
                    <font-awesome-icon icon="clock" />
                    {{ $t("chat.screenshotViewTime", { seconds: message.ttl }) }}
                  </div>
                  <div class="button view-btn" @click="viewScreenshot(message)">
                    {{ $t("chat.screenshotView") }}
                  </div>
                </div>
                <div v-else class="screenshot-viewed">
                  <font-awesome-icon icon="image" />
                  <div class="screenshot-label">{{ $t("chat.screenshotLabel") }}</div>
                  <div v-if="message.mode === 'timer'" class="screenshot-countdown">
                    <font-awesome-icon icon="clock" />
                    {{ getRemainingSeconds(message) }}{{ $t("chat.screenshotSecondsLeft") }}
                  </div>
                  <div class="button view-btn" @click="viewScreenshot(message)">
                    {{ $t("chat.screenshotViewAgain") }}
                  </div>
                </div>
              </template>
            </div>
            <div class="message-time">
              {{ formatTime(message.timestamp) }}
            </div>
          </template>
        </div>
        <div v-if="currentChat.messages.length === 0" class="no-messages">
          {{ $t("chat.noMessages") }}
        </div>
      </div>

      <div class="chat-input" v-show="!isMinimized && !screenshotPreview">
        <input
          type="text"
          v-model="messageInput"
          :placeholder="$t('chat.typeMessage')"
          @keyup.enter="sendMessage"
          @focus="isInputFocused = true"
          @blur="isInputFocused = false"
          maxlength="500"
        />
        <div
          class="button"
          @click="sendMessage"
          :class="{ disabled: !messageInput.trim() }"
        >
          <font-awesome-icon icon="paper-plane" />
          {{ $t("chat.send") }}
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapMutations, mapState } from "vuex";

export default {
  data() {
    return {
      messageInput: "",
      isMinimized: false,
      isInputFocused: false,
      keyboardHeight: 0,
      screenshotPreview: null,
      screenshotMode: "timer",
      screenshotTTL: 10,
      countdownTimer: null,
      countdownTick: 0,
    };
  },
  computed: {
    ...mapState(["modals", "session"]),
    ...mapState("players", ["players"]),
    currentChat() {
      if (!this.session.currentChatPlayerId) return null;
      return this.session.chats[this.session.currentChatPlayerId];
    },
    isStoryteller() {
      return !this.session.isSpectator;
    },
  },
  watch: {
    "currentChat.messages": function () {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    "session.currentChatPlayerId": function (newPlayerId) {
      if (newPlayerId) {
        this.isMinimized = false;
        this.$store.commit("session/setChatMinimized", false);
        this.$store.commit("session/markChatAsRead", newPlayerId);
      }
    },
    "session.isChatMinimized": function (isMinimized) {
      this.isMinimized = isMinimized;
    },
  },
  methods: {
    getChatTitle() {
      if (!this.session.currentChatPlayerId) return "";

      if (this.session.currentChatPlayerId === "storyteller") {
        return `${this.$t("chat.privateChatWith")} ${this.$t(
          "session.storyteller",
        )}`;
      }

      const player = this.players.find(
        (p) => p.id === this.session.currentChatPlayerId,
      );
      if (!player) return this.$t("chat.privateChatTitle");

      const playerIndex = this.players.indexOf(player);
      const seatNumber = playerIndex + 1;
      const playerName =
        player.name === "__EMPTY_SEAT__"
          ? this.$t("player.emptySeatName")
          : player.name;

      return `${this.$t("chat.privateChatWith")} ${seatNumber}. ${playerName}`;
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    },
    sendMessage() {
      const content = this.messageInput.trim();
      if (!content || !this.session.currentChatPlayerId) return;

      this.$store.commit("session/sendChatMessage", {
        targetPlayerId: this.session.currentChatPlayerId,
        content,
      });

      this.messageInput = "";
    },
    handlePaste(e) {
      if (!this.isStoryteller) return;
      const items = e.clipboardData && e.clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          this.compressImage(blob);
          return;
        }
      }
    },
    compressImage(blob) {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement("canvas");
        const maxWidth = 1200;
        const maxHeight = 900;
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        this.screenshotPreview = canvas.toDataURL("image/jpeg", 0.7);
      };
      img.src = url;
    },
    cancelScreenshot() {
      this.screenshotPreview = null;
      this.screenshotMode = "timer";
      this.screenshotTTL = 10;
    },
    sendScreenshot() {
      if (!this.screenshotPreview || !this.session.currentChatPlayerId) return;
      const msgId = Math.random().toString(36).substr(2) + Date.now().toString(36);
      this.$store.commit("session/sendScreenshotMessage", {
        targetPlayerId: this.session.currentChatPlayerId,
        msgId,
        imageData: this.screenshotPreview,
        mode: this.screenshotMode,
        ttl: this.screenshotMode === "timer" ? this.screenshotTTL : null,
      });
      this.screenshotPreview = null;
      this.screenshotMode = "timer";
      this.screenshotTTL = 10;
    },
    viewScreenshot(message) {
      if (message.revoked || !message.imageData) return;
      if (this.isScreenshotExpired(message)) return;
      if (!message.viewedAt) {
        this.$store.commit("session/screenshotViewed", {
          chatPlayerId: this.session.currentChatPlayerId,
          msgId: message.msgId,
        });
      }
      this.$store.commit("session/openScreenshotViewer", {
        msgId: message.msgId,
        imageData: message.imageData,
        mode: message.mode,
        ttl: message.ttl,
        viewedAt: message.viewedAt || Date.now(),
      });
    },
    revokeScreenshot(message) {
      this.$store.commit("session/revokeScreenshot", {
        targetPlayerId: this.session.currentChatPlayerId,
        msgId: message.msgId,
      });
    },
    isScreenshotExpired(message) {
      // Reference countdownTick to trigger Vue reactivity on each tick
      void this.countdownTick;
      if (message.mode !== "timer" || !message.viewedAt) return false;
      const elapsed = (Date.now() - message.viewedAt) / 1000;
      return elapsed >= message.ttl;
    },
    getRemainingSeconds(message) {
      void this.countdownTick;
      if (!message.viewedAt || message.mode !== "timer") return message.ttl;
      const elapsed = (Date.now() - message.viewedAt) / 1000;
      const remaining = Math.max(0, Math.ceil(message.ttl - elapsed));
      return remaining;
    },
    startCountdownTicker() {
      if (this.countdownTimer) return;
      this.countdownTimer = setInterval(() => {
        this.countdownTick++;
        this.checkExpiredScreenshots();
      }, 1000);
    },
    checkExpiredScreenshots() {
      if (!this.currentChat) return;
      const chatPlayerId = this.session.currentChatPlayerId;
      this.currentChat.messages.forEach((msg) => {
        if (
          msg.type === "screenshot" &&
          msg.mode === "timer" &&
          msg.viewedAt &&
          !msg.revoked &&
          msg.imageData
        ) {
          const elapsed = (Date.now() - msg.viewedAt) / 1000;
          if (elapsed >= msg.ttl) {
            this.$store.commit("session/screenshotExpired", {
              chatPlayerId,
              msgId: msg.msgId,
            });
          }
        }
      });
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      this.$store.commit("session/setChatMinimized", this.isMinimized);
      if (!this.isMinimized && this.session.currentChatPlayerId) {
        this.$store.commit("session/markChatAsRead", this.session.currentChatPlayerId);
      }
    },
    closeChat() {
      this.$store.commit(
        "session/markChatAsRead",
        this.session.currentChatPlayerId,
      );
      this.$store.commit("session/setCurrentChat", null);
      this.$store.commit("session/setChatMinimized", false);
      this.isMinimized = false;
      this.screenshotPreview = null;
      this.toggleModal("chat");
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    handleViewportResize() {
      if (window.visualViewport && this.isInputFocused) {
        const windowHeight = window.innerHeight;
        const viewportHeight = window.visualViewport.height;
        this.keyboardHeight = windowHeight - viewportHeight;
      } else {
        this.keyboardHeight = 0;
      }
    },
    ...mapMutations(["toggleModal"]),
  },
  mounted() {
    this.isMinimized = this.session.isChatMinimized;
    this.scrollToBottom();
    this.startCountdownTicker();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportResize);
      window.visualViewport.addEventListener('scroll', this.handleViewportResize);
    }
  },
  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize);
      window.visualViewport.removeEventListener('scroll', this.handleViewportResize);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../vars.scss";

.chat-modal {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 350px;
  background: rgba(0, 0, 0, 0.95);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 767px) {
    width: calc(100vw - 20px);
    right: 10px;
    bottom: 10px;
    max-width: 400px;

    &.input-focused {
      max-height: calc(100vh - var(--keyboard-height, 0px) - 20px);

      .chat-messages {
        max-height: calc(100vh - var(--keyboard-height, 0px) - 180px);
      }
    }
  }

  @media (max-width: 480px) {
    width: calc(100vw - 20px);
    max-width: none;
  }

  .chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 15px;
    background: rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .chat-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 15px;
      font-weight: 500;
      transition: all 0.2s;

      &.clickable {
        cursor: pointer;
        user-select: none;

        &:hover {
          color: #60a5fa;
          transform: translateY(-1px);
        }

        &:active {
          transform: translateY(0);
        }
      }

      .unread-badge-header {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #ff4444;
        color: white;
        border-radius: 10px;
        padding: 2px 7px;
        font-size: 11px;
        font-weight: bold;
        min-width: 20px;
        height: 20px;
        border: 2px solid white;
        margin-left: 4px;
      }
    }

    .chat-controls {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .minimize-btn,
    .close-btn {
      cursor: pointer;
      opacity: 0.7;
      transition: all 0.2s;
      font-size: 20px;
      padding: 4px;

      &:hover {
        opacity: 1;
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95);
      }
    }

    .minimize-btn:hover {
      color: #2563eb;
    }

    .close-btn:hover {
      color: #ff4444;
    }
  }

  .screenshot-config {
    padding: 15px;
    background: rgba(0, 0, 0, 0.4);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .screenshot-preview {
      margin-bottom: 12px;
      border-radius: 8px;
      overflow: hidden;
      max-height: 150px;

      img {
        width: 100%;
        height: auto;
        max-height: 150px;
        object-fit: contain;
        display: block;
      }
    }

    .screenshot-options {
      margin-bottom: 12px;

      .radio-option {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        font-size: 13px;
        cursor: pointer;

        input[type="radio"] {
          accent-color: #2563eb;
        }
      }

      .ttl-input {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: 24px;
        font-size: 12px;

        input[type="number"] {
          width: 60px;
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          font-size: 12px;
          text-align: center;
        }
      }
    }

    .screenshot-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;

      .button {
        padding: 8px 14px;
        border-radius: 6px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s;
      }

      .cancel-btn {
        background: rgba(255, 255, 255, 0.1);

        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }

      .send-btn {
        background: #2563eb;

        &:hover {
          background: #1d4ed8;
        }

        svg {
          margin-right: 4px;
        }
      }
    }
  }

  .chat-messages {
    max-height: 350px;
    overflow-y: auto;
    padding: 15px;
    background: rgba(0, 0, 0, 0.3);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    }

    .no-messages {
      text-align: center;
      opacity: 0.5;
      padding: 30px 20px;
      font-size: 13px;
    }

    .message {
      margin-bottom: 12px;
      padding: 10px 12px;
      border-radius: 12px;
      max-width: 85%;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

      &.sent {
        margin-left: auto;
        background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
        border: none;
      }

      &.received {
        margin-right: auto;
        background: rgba(55, 65, 81, 0.9);
        border: none;
      }

      .message-sender {
        font-size: 11px;
        font-weight: 600;
        margin-bottom: 4px;
        opacity: 0.8;
      }

      .message-content {
        word-wrap: break-word;
        line-height: 1.4;
      }

      .message-time {
        font-size: 10px;
        opacity: 0.5;
        margin-top: 4px;
        text-align: right;
      }

      .screenshot-message {
        .screenshot-expired {
          padding: 12px;
          text-align: center;
          opacity: 0.6;
          font-size: 12px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;

          svg {
            margin-right: 6px;
          }
        }

        .screenshot-thumbnail {
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 8px;

          img {
            width: 100%;
            height: auto;
            max-height: 120px;
            object-fit: cover;
            display: block;
            filter: brightness(0.8);
          }
        }

        .screenshot-status {
          font-size: 11px;
          opacity: 0.8;
          display: flex;
          align-items: center;
          justify-content: space-between;

          svg {
            margin-right: 4px;
          }

          .revoke-btn {
            padding: 4px 10px;
            font-size: 11px;
            background: #dc2626;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
              background: #b91c1c;
            }

            svg {
              margin-right: 4px;
            }
          }
        }

        .screenshot-hidden,
        .screenshot-viewed {
          padding: 12px;
          text-align: center;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;

          > svg {
            font-size: 24px;
            opacity: 0.6;
            margin-bottom: 8px;
          }

          .screenshot-label {
            font-size: 13px;
            font-weight: 500;
            margin-bottom: 6px;
          }

          .screenshot-timer-info,
          .screenshot-countdown {
            font-size: 11px;
            opacity: 0.7;
            margin-bottom: 8px;

            svg {
              margin-right: 4px;
            }
          }

          .view-btn {
            display: inline-block;
            padding: 6px 16px;
            font-size: 12px;
            background: #2563eb;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
              background: #1d4ed8;
            }
          }
        }
      }
    }
  }

  .chat-input {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 12px 15px;
    background: rgba(255, 255, 255, 0.03);
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    input {
      flex: 1;
      padding: 10px 12px;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      background: rgba(0, 0, 0, 0.5);
      color: white;
      font-size: 13px;

      &:focus {
        outline: none;
        border-color: #2563eb;
        background: rgba(0, 0, 0, 0.6);
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.3);
      }
    }

    .button {
      padding: 10px 16px;
      cursor: pointer;
      white-space: nowrap;
      font-size: 13px;
      background: #2563eb;
      border-radius: 8px;
      transition: all 0.2s;
      border: none;

      &:hover:not(.disabled) {
        background: #1d4ed8;
        transform: translateY(-1px);
      }

      &.disabled {
        opacity: 0.4;
        cursor: not-allowed;
        background: #666;
      }

      svg {
        margin-right: 5px;
      }
    }
  }
}

// Slide fade animation
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
